import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@pluggyai/ui'
import { AxiosError, isAxiosError } from 'axios'
import { Grid } from 'semantic-ui-react'

import { EXECUTION_ERROR } from '../../../../../modules/item/types'
import {
  isAilosCreditCardMaxDeviceLimitReachedError,
  isCaixaAuthorizedDevicesLimitReachedError,
} from '../../../../../modules/item/utils'
import {
  connectionErrorsMap,
  isExecutionError,
  mapExecutionStatusToStep,
} from '../utils'
import { AilosDeviceConnectionLimitReachedError } from './AilosDeviceConnectionLimitReachedError'
import { AuthorizedDevicesLimitReached } from './AuthorizedDevicesLimitReached'
import { ConnectTokenExpiredError } from './ConnectTokenExpiredError'
import { DeviceAuthorizationNeededStatus } from './DeviceAuthorizationNeededStatus'
import { Props } from './ErrorStatus.types'

import './ErrorStatus.css'

const ErrorStatus = ({
  item,
  itemError,
  connector,
  onGoToConnectorsList,
  onRetry,
  onClose,
  displayInstitutionLogos,
}: Props) => {
  const { t } = useTranslation()

  let currentItemExecutionError: EXECUTION_ERROR | undefined
  if (item) {
    const currentStep = mapExecutionStatusToStep(item)
    if (isExecutionError(currentStep)) {
      currentItemExecutionError = currentStep
    }
  }

  const errorMessage = t(
    connectionErrorsMap[currentItemExecutionError || 'ERROR'].i18nKey,
    {
      connectorName: connector.name,
    },
  )

  const isDeviceAuthorizationError =
    item?.executionStatus === 'USER_AUTHORIZATION_PENDING' ||
    item?.executionStatus === 'USER_AUTHORIZATION_NOT_GRANTED'

  const itemAxiosError: AxiosError | undefined =
    itemError && isAxiosError(itemError) ? itemError : undefined

  const isConnectTokenUnauthorizedError =
    itemAxiosError?.response?.status !== undefined &&
    itemAxiosError.response.status === 403

  return (
    <div className={'ErrorStatus'}>
      {item && isAilosCreditCardMaxDeviceLimitReachedError(item) ? (
        <AilosDeviceConnectionLimitReachedError
          connector={item.connector}
          onClose={onClose}
          displayInstitutionLogo={displayInstitutionLogos}
          onRetry={onRetry}
        />
      ) : isDeviceAuthorizationError ? (
        <DeviceAuthorizationNeededStatus
          onClose={onClose}
          item={item}
          connector={connector}
          onRetry={onRetry}
        />
      ) : isConnectTokenUnauthorizedError ? (
        <ConnectTokenExpiredError onClose={onClose} />
      ) : isCaixaAuthorizedDevicesLimitReachedError(item) ? (
        <AuthorizedDevicesLimitReached onRetry={onRetry} onClose={onClose} />
      ) : (
        <>
          {/* General error status */}
          <Grid
            className={'error-container'}
            columns={2}
            verticalAlign={'middle'}
            centered={true}
            textAlign={'left'}
          >
            <Grid.Row className="error-image">
              <img
                src={`../${
                  currentItemExecutionError === 'SITE_NOT_AVAILABLE'
                    ? 'site_not_available.png'
                    : 'error.png'
                }`}
                alt="Oops! Connection error"
              />
            </Grid.Row>
            <Grid.Row className="error-message">
              <p className={currentItemExecutionError || 'api-error'}>
                {errorMessage}
              </p>
              <pre className={'error-code'}>ErrorCode: {item?.id}</pre>
            </Grid.Row>
          </Grid>

          <div className="action-container">
            {/* TODO refactor to track event on all button click cases */}
            {currentItemExecutionError === 'SITE_NOT_AVAILABLE' ? (
              <Button primary onClick={onGoToConnectorsList}>
                {t('connectStatus.action.try-another-provider')}
              </Button>
            ) : currentItemExecutionError === 'CONNECTION_ERROR' ? (
              <Button primary onClick={onRetry}>
                {t('connectStatus.action.retry')}
              </Button>
            ) : (
              <Button primary onClick={onClose}>
                {t('connectStatus.action.close')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default React.memo(ErrorStatus)
